/**
 * Custom hook for managing create solicitud form state and logic
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { solicitudesAPI, proyectosAPI, subdivisionesAPI, catalogosAPI } from '../../utils/api';
import { toast } from 'sonner';

export const ORIGENES = [
  'Portal del Propietario',
  'Acta de Entrega',
  'Observación de Entrega',
  'Sperant',
  'Portal de Negocios'
];

// Pure function for warranty calculation
export const calcularEstadosGarantia = (fechaEntrega, origen, proyecto, subdivision) => {
  if (!fechaEntrega || !proyecto) {
    return { acabados: '', estructural: '' };
  }
  
  if (origen === 'Observación de Entrega' || origen === 'Acta de Entrega') {
    return { acabados: 'No Identificado', estructural: 'No Identificado' };
  }

  if (proyecto.tiene_subdivision && !subdivision) {
    return { acabados: '', estructural: '' };
  }

  const fechaEntregaDate = new Date(fechaEntrega);
  const fechaActual = new Date();
  
  let garantiaAcabadosAnios, garantiaAcabadosMeses;
  let garantiaEstructuralAnios, garantiaEstructuralMeses;
  
  if (proyecto.tiene_subdivision && subdivision) {
    garantiaAcabadosAnios = subdivision.garantia_acabados_anios ?? 1;
    garantiaAcabadosMeses = subdivision.garantia_acabados_meses ?? 0;
    garantiaEstructuralAnios = subdivision.garantia_estructural_anios ?? 10;
    garantiaEstructuralMeses = subdivision.garantia_estructural_meses ?? 0;
  } else {
    garantiaAcabadosAnios = proyecto.garantia_acabados_anios ?? 1;
    garantiaAcabadosMeses = proyecto.garantia_acabados_meses ?? 0;
    garantiaEstructuralAnios = proyecto.garantia_estructural_anios ?? 10;
    garantiaEstructuralMeses = proyecto.garantia_estructural_meses ?? 0;
  }
  
  const fechaFinAcabados = new Date(fechaEntregaDate);
  fechaFinAcabados.setFullYear(fechaFinAcabados.getFullYear() + garantiaAcabadosAnios);
  fechaFinAcabados.setMonth(fechaFinAcabados.getMonth() + garantiaAcabadosMeses);
  
  const fechaFinEstructural = new Date(fechaEntregaDate);
  fechaFinEstructural.setFullYear(fechaFinEstructural.getFullYear() + garantiaEstructuralAnios);
  fechaFinEstructural.setMonth(fechaFinEstructural.getMonth() + garantiaEstructuralMeses);
  
  const estadoAcabados = fechaActual <= fechaFinAcabados ? 'Vigente' : 'Vencido';
  const estadoEstructural = fechaActual <= fechaFinEstructural ? 'Vigente' : 'Vencido';
  
  return { acabados: estadoAcabados, estructural: estadoEstructural };
};

export const useCreateSolicitud = (onClose) => {
  const [proyectos, setProyectos] = useState([]);
  const [subdivisiones, setSubdivisiones] = useState([]);
  const [titularEsSolicitante, setTitularEsSolicitante] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true); // Nuevo: spinner de carga inicial
  const [formData, setFormData] = useState({
    proyecto_id: '',
    etapa: '',
    departamento: '',
    cliente_nombre: '',
    cliente_email: '',
    cliente_telefono: '',
    solicitante_nombre: '',
    solicitante_email: '',
    solicitante_telefono: '',
    origen: 'Portal de Negocios',
    fecha_entrega: '',
    estado_garantia_acabados: '',
    estado_garantia_estructural: '',
  });
  const [solicitudes, setSolicitudes] = useState([
    { categoria: '', lugar: '', item: '', falla: '', descripcion: '', fotos: [] }
  ]);
  const [loading, setLoading] = useState(false);
  const [calculandoGarantia, setCalculandoGarantia] = useState(false);
  const [selectedProyecto, setSelectedProyecto] = useState(null);
  const [selectedSubdivision, setSelectedSubdivision] = useState(null);
  
  // Catálogos state
  const [categorias, setCategorias] = useState([]);
  const [lugares, setLugares] = useState([]);
  const [items, setItems] = useState([]);
  const [fallas, setFallas] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(false);
  
  // Refs for avoiding stale closures
  const selectedProyectoRef = useRef(null);
  const selectedSubdivisionRef = useRef(null);
  
  useEffect(() => {
    selectedProyectoRef.current = selectedProyecto;
  }, [selectedProyecto]);
  
  useEffect(() => {
    selectedSubdivisionRef.current = selectedSubdivision;
  }, [selectedSubdivision]);

  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      try {
        await Promise.all([
          loadProyectos(),
          loadCatalogos()
        ]);
      } finally {
        setInitialLoading(false);
      }
    };
    
    loadInitialData();
  }, []);

  // Load all catalogs
  const loadCatalogos = async () => {
    setLoadingCatalogos(true);
    try {
      const [catRes, lugRes, itemRes, falRes] = await Promise.all([
        catalogosAPI.getCategorias(),
        catalogosAPI.getLugares(),
        catalogosAPI.getItems(),
        catalogosAPI.getFallas()
      ]);
      setCategorias(catRes.data || []);
      setLugares(lugRes.data || []);
      setItems(itemRes.data || []);
      setFallas(falRes.data || []);
    } catch (error) {
      console.error('Error loading catalogos:', error);
    } finally {
      setLoadingCatalogos(false);
    }
  };

  // Execute warranty calculation with spinner
  const ejecutarCalculoGarantia = useCallback((fechaEntrega, origen, proyecto, subdivision) => {
    if (!fechaEntrega || !proyecto) return;
    
    setCalculandoGarantia(true);
    
    setTimeout(() => {
      const resultado = calcularEstadosGarantia(fechaEntrega, origen, proyecto, subdivision);
      
      setFormData(prev => ({
        ...prev,
        estado_garantia_acabados: resultado.acabados,
        estado_garantia_estructural: resultado.estructural
      }));
      
      setCalculandoGarantia(false);
    }, 300);
  }, []);

  const loadProyectos = async () => {
    try {
      const response = await proyectosAPI.getAll();
      setProyectos(response.data);
    } catch (error) {
      toast.error('Error al cargar proyectos');
    }
  };

  const loadSubdivisiones = async (proyectoId) => {
    try {
      const response = await subdivisionesAPI.getByProyecto(proyectoId);
      setSubdivisiones(response.data || []);
    } catch (error) {
      setSubdivisiones([]);
    }
  };

  const handleProyectoChange = async (proyectoId) => {
    const proyecto = proyectos.find(p => p.id === proyectoId);
    
    setSelectedProyecto(proyecto);
    setSelectedSubdivision(null);
    selectedProyectoRef.current = proyecto;
    selectedSubdivisionRef.current = null;
    
    setFormData(prev => ({ 
      ...prev, 
      proyecto_id: proyectoId, 
      etapa: '', 
      estado_garantia_acabados: '', 
      estado_garantia_estructural: '' 
    }));
    
    if (proyecto?.tiene_subdivision) {
      await loadSubdivisiones(proyectoId);
    } else {
      setSubdivisiones([]);
      setTimeout(() => {
        const currentFecha = formData.fecha_entrega;
        if (currentFecha && proyecto) {
          ejecutarCalculoGarantia(currentFecha, formData.origen, proyecto, null);
        }
      }, 50);
    }
  };

  const handleEtapaChange = (etapaValue) => {
    const etapaFinal = etapaValue === "sin_etapas" ? "Sin etapas" : etapaValue;
    
    let subdivision = null;
    
    if (selectedProyectoRef.current?.tiene_subdivision) {
      subdivision = subdivisiones.find(s => s.nombre === etapaValue) || null;
      setSelectedSubdivision(subdivision);
      selectedSubdivisionRef.current = subdivision;
    }
    
    setFormData(prev => ({ 
      ...prev, 
      etapa: etapaFinal,
      estado_garantia_acabados: '', 
      estado_garantia_estructural: '' 
    }));
    
    setTimeout(() => {
      const fechaEntrega = formData.fecha_entrega;
      if (fechaEntrega && selectedProyectoRef.current) {
        ejecutarCalculoGarantia(fechaEntrega, formData.origen, selectedProyectoRef.current, subdivision);
      }
    }, 50);
  };
  
  const handleFechaEntregaChange = (nuevaFecha) => {
    setFormData(prev => ({ ...prev, fecha_entrega: nuevaFecha }));
    
    if (nuevaFecha && selectedProyectoRef.current) {
      ejecutarCalculoGarantia(
        nuevaFecha, 
        formData.origen, 
        selectedProyectoRef.current, 
        selectedSubdivisionRef.current
      );
    } else {
      setFormData(prev => ({
        ...prev,
        fecha_entrega: nuevaFecha,
        estado_garantia_acabados: '',
        estado_garantia_estructural: ''
      }));
    }
  };
  
  const handleOrigenChange = (nuevoOrigen) => {
    setFormData(prev => ({ ...prev, origen: nuevoOrigen }));
    
    const fechaEntrega = formData.fecha_entrega;
    if (fechaEntrega && selectedProyectoRef.current) {
      ejecutarCalculoGarantia(
        fechaEntrega, 
        nuevoOrigen, 
        selectedProyectoRef.current, 
        selectedSubdivisionRef.current
      );
    }
  };

  const handleTitularEsSolicitanteChange = (checked) => {
    setTitularEsSolicitante(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        solicitante_nombre: '',
        solicitante_email: '',
        solicitante_telefono: '',
      }));
    }
  };

  const addSolicitud = () => {
    setSolicitudes(prev => [...prev, { categoria: '', lugar: '', item: '', falla: '', descripcion: '', fotos: [] }]);
  };

  const removeSolicitud = (index) => {
    if (solicitudes.length > 1) {
      setSolicitudes(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateSolicitud = (index, field, value) => {
    setSolicitudes(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const updateSolicitudFotos = (index, fotos) => {
    setSolicitudes(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], fotos };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validSolicitudes = solicitudes.filter(s => s.categoria && s.lugar && s.item && s.falla && s.descripcion);
    if (validSolicitudes.length === 0) {
      toast.error('Debe agregar al menos una solicitud completa');
      return;
    }

    setLoading(true);
    try {
      const solicitudesWithFotos = validSolicitudes.map(s => ({
        categoria: s.categoria,
        lugar: s.lugar,
        item: s.item,
        falla: s.falla,
        descripcion: s.descripcion,
        foto_url: s.fotos && s.fotos.length > 0 ? JSON.stringify(s.fotos.map(f => f.url || f)) : null
      }));

      const submitData = {
        ...formData,
        solicitudes: solicitudesWithFotos
      };
      
      if (titularEsSolicitante) {
        submitData.solicitante_nombre = formData.cliente_nombre;
        submitData.solicitante_email = formData.cliente_email;
        submitData.solicitante_telefono = formData.cliente_telefono;
      }
      
      await solicitudesAPI.create(submitData);
      toast.success('Requerimiento creado exitosamente');
      onClose();
    } catch (error) {
      toast.error('Error al crear requerimiento');
    } finally {
      setLoading(false);
    }
  };

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return {
    // State
    proyectos,
    subdivisiones,
    titularEsSolicitante,
    formData,
    solicitudes,
    loading,
    initialLoading, // Agregado
    calculandoGarantia,
    selectedProyecto,
    selectedSubdivision,
    
    // Catálogos
    categorias,
    lugares,
    items,
    fallas,
    loadingCatalogos,
    
    // Handlers
    handleProyectoChange,
    handleEtapaChange,
    handleFechaEntregaChange,
    handleOrigenChange,
    handleTitularEsSolicitanteChange,
    addSolicitud,
    removeSolicitud,
    updateSolicitud,
    updateSolicitudFotos,
    handleSubmit,
    updateFormField
  };
};

export default useCreateSolicitud;
